import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  OutlinedInput,
  Alert,
  Snackbar,
  Skeleton,
  Paper,
  CircularProgress,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { getMenuConfig, setMenuConfig } from "../api";

/* Role LEVELS 1–9 with their names (mirrors the backend role hierarchy). */
const ROLES = [
  { level: 1, name: "ADMIN" },
  { level: 2, name: "ADMIN_PARTNER" },
  { level: 3, name: "WHITE_LABEL" },
  { level: 4, name: "STATE_HEAD" },
  { level: 5, name: "SUPER_DISTRIBUTOR" },
  { level: 6, name: "MASTER_DISTRIBUTOR" },
  { level: 7, name: "DISTRIBUTOR" },
  { level: 8, name: "RETAILER" },
  { level: 9, name: "SHOPKEEPER" },
];

const roleName = (level) =>
  (ROLES.find((r) => r.level === level)?.name || `L${level}`).replace(/_/g, " ");

/* The user-facing menu items (label + path) — path is used as the menuKey.
   Keep this in sync with the userMenu array in components/Sidebar.jsx. */
/* Only the pages BiznessPay actually uses — Pay Bills / Card to Bank / My Cards
   are not part of this project, so they're not toggleable here. */
const USER_MENU = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "My Profile", path: "/profile" },
  { label: "Add User", path: "/invite" },
  { label: "My Downline", path: "/downline" },
  { label: "Wallet", path: "/transactions" },
  { label: "Passbook", path: "/passbook" },
  { label: "Commissions", path: "/commissions" },
  { label: "Payins", path: "/payins" },
  { label: "Payouts", path: "/payouts" },
];

export default function AdminMenuAccess() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getMenuConfig()
      .then(({ data }) => setConfig(data.menu || {}))
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load menu configuration"),
      )
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (path, value) => {
    /* MUI multi-select gives back an array of the selected level numbers. */
    const levels = (typeof value === "string" ? value.split(",") : value)
      .map(Number)
      .sort((a, b) => a - b);
    setConfig((prev) => ({ ...prev, [path]: levels }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    /* Only persist restricted items; an empty selection means visible to all,
       so we drop the key entirely. */
    const menu = {};
    USER_MENU.forEach((item) => {
      const levels = config[item.path];
      if (Array.isArray(levels) && levels.length > 0) menu[item.path] = levels;
    });
    try {
      await setMenuConfig(menu);
      setSnack("Menu access saved");
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save menu configuration");
    }
    setSaving(false);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Menu Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which roles can see each menu item. Leave a menu empty to show
            it to everyone.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveRoundedIcon />
            )
          }
          sx={{ borderRadius: 2.5, fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, borderRadius: 3 }}>
        {loading ? (
          <Stack spacing={1.5}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {USER_MENU.map((item) => {
              const selected = config[item.path] || [];
              return (
                <Paper
                  key={item.path}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: 2.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: 160, flex: "0 0 auto" }}>
                    <Typography fontWeight={700} fontSize={15}>
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {item.path}
                    </Typography>
                  </Box>
                  <FormControl sx={{ flex: 1, minWidth: 240 }} size="small">
                    <InputLabel id={`roles-${item.path}`}>
                      {selected.length === 0
                        ? "Visible to all roles"
                        : "Allowed roles"}
                    </InputLabel>
                    <Select
                      labelId={`roles-${item.path}`}
                      multiple
                      value={selected}
                      onChange={(e) => handleChange(item.path, e.target.value)}
                      input={
                        <OutlinedInput
                          label={
                            selected.length === 0
                              ? "Visible to all roles"
                              : "Allowed roles"
                          }
                        />
                      }
                      renderValue={(vals) =>
                        vals.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Everyone
                          </Typography>
                        ) : (
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {vals.map((lvl) => (
                              <Chip
                                key={lvl}
                                size="small"
                                label={roleName(lvl)}
                                sx={{ fontWeight: 600, fontSize: 11 }}
                              />
                            ))}
                          </Stack>
                        )
                      }
                    >
                      {ROLES.map((r) => (
                        <MenuItem key={r.level} value={r.level}>
                          <Checkbox checked={selected.indexOf(r.level) > -1} />
                          <ListItemText
                            primary={r.name.replace(/_/g, " ")}
                            secondary={`Level ${r.level}`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Card>

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        mt={2}
        sx={{ color: "text.secondary" }}
      >
        <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          Admins always see every menu item regardless of these settings.
        </Typography>
      </Stack>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ borderRadius: 2 }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
}
